import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Row.module.sass";
import cn from "classnames";
import Checkbox from "../../../../../components/Checkbox";
import Icon from "../../../../../components/Icon";
import Actions from "../../../../../components/Actions";
import LetterAvatar from "../../../../../components/LetterAvatar";
import ModalSignAttendance from "../../../../../components/ModalSignAttendance/ModalSignAttendance";
import ModalAttendanceComment from "../../../../../components/ModalAttendanceComment";
import { updateAttendance } from "../../../../../services/attendances.service";
import { showSuccess, showError } from "../../../../../services/toast.service";

const Row = ({ item, value, onChange, onEdit, onDelete, onSendSignatureInvitation, onAttendanceUpdate, session }) => {
    const navigate = useNavigate();
    const [showSignModal, setShowSignModal] = useState(false);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(item);
    const [isUpdating, setIsUpdating] = useState(false);

    // Handler pour changer le statut de l'attendance
    const handleStatusChange = async (newStatus, statusJustification = '') => {
        if (isUpdating) return;
        
        try {
            setIsUpdating(true);
            
            // Préparer les données de mise à jour
            const updateData = {
                status: newStatus,
                status_justification: statusJustification,
                signed_at: null,
                signature_file: null,
                user_agent: null
            };
            
            await updateAttendance(currentItem.id, updateData);
            
            // Mettre à jour l'état local
            setCurrentItem(prev => ({
                ...prev,
                status: newStatus,
                status_justification: statusJustification,
                signed_at: null,
                signature_file: null
            }));
            
            // Notifier le parent
            if (onAttendanceUpdate) {
                onAttendanceUpdate(currentItem.id);
            }
            
            const statusLabel = statusJustification ? `${newStatus} (${statusJustification})` : newStatus;
            showSuccess(`Statut mis à jour: ${statusLabel}`);
            
        } catch (error) {
            console.error('Erreur lors de la mise à jour du statut:', error);
            showError('Erreur lors de la mise à jour du statut');
        } finally {
            setIsUpdating(false);
        }
    };

    // Handler pour sauvegarder un commentaire
    const handleSaveComment = async (commentText) => {
        try {
            await updateAttendance(currentItem.id, { comments: commentText });
            
            setCurrentItem(prev => ({
                ...prev,
                comments: commentText
            }));
            
            if (onAttendanceUpdate) {
                onAttendanceUpdate(currentItem.id);
            }
            
            showSuccess('Commentaire enregistré');
            setShowCommentModal(false);
        } catch (error) {
            console.error('Erreur lors de l\'enregistrement du commentaire:', error);
            showError('Erreur lors de l\'enregistrement du commentaire');
        }
    };
    
    // Écouter les mises à jour de statut d'attendance
    useEffect(() => {
        const handleAttendanceUpdate = (event) => {
            const { attendanceId, newStatus, hasSigned } = event.detail;
            
            // Si c'est notre attendance qui a été mise à jour
            if (attendanceId === currentItem.id) {
                setCurrentItem(prev => ({
                    ...prev,
                    status: newStatus,
                    signature_file: hasSigned ? 'signed' : prev.signature_file
                }));
            }
        };

        window.addEventListener('attendanceStatusUpdated', handleAttendanceUpdate);
        
        return () => {
            window.removeEventListener('attendanceStatusUpdated', handleAttendanceUpdate);
        };
    }, [currentItem.id]);

    // Mettre à jour l'item local quand les props changent
    useEffect(() => {
        setCurrentItem(item);
    }, [item]);
    // Récupérer les informations du participant depuis l'expansion
    const participant = currentItem.expand?.participant_id;
    
    // Fonction pour formater le statut de présence
    const getPresenceStatus = (status, statusJustification) => {
        const statusLower = status?.toLowerCase();
        const isJustified = statusJustification && statusJustification !== 'Absence injustifiée';
        
        if (statusLower === 'présent' || statusLower === 'present') {
            return (
                <div className={cn('status-type', 'status-green-dark')}>
                    Présent
                </div>
            );
        } else if (statusLower === 'absent') {
            return (
                <div className={cn('status-type', isJustified ? 'status-yellow-dark' : 'status-red-dark')}>
                    {isJustified ? 'Abs. justifiée' : 'Absent'}
                </div>
            );
        } else if (statusLower === 'en retard') {
            return (
                <div className={cn('status-type', 'status-yellow-dark')}>
                    En retard
                </div>
            );
        } else if (statusLower === 'départ anticipé') {
            return (
                <div className={cn('status-type', 'status-yellow-dark')}>
                    Départ anticipé
                </div>
            );
        } else {
            return (
                <div className={cn('status-type', 'status-gray-dark')}>
                    {status || 'Non défini'}
                </div>
            );
        }
    };

    // Marquer départ anticipé - utilise comments car status_justification est un select
    const handleEarlyDeparture = async () => {
        if (isUpdating) return;
        
        try {
            setIsUpdating(true);
            const currentTime = new Date().toLocaleTimeString('fr-FR', { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
            
            // status_justification est un select, donc on utilise comments pour l'heure
            const updateData = {
                status: 'Départ anticipé',
                comments: `Départ anticipé à ${currentTime}`
            };
            
            await updateAttendance(currentItem.id, updateData);
            
            setCurrentItem(prev => ({
                ...prev,
                status: 'Départ anticipé',
                comments: `Départ anticipé à ${currentTime}`
            }));
            
            if (onAttendanceUpdate) {
                onAttendanceUpdate(currentItem.id);
            }
            
            showSuccess(`Départ anticipé enregistré à ${currentTime}`);
        } catch (error) {
            console.error('Erreur lors du marquage départ anticipé:', error);
            showError('Erreur lors du marquage départ anticipé');
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <>
            <div className={styles.row}>
                <div className={styles.col}>
                    <Checkbox
                        className={styles.checkbox}
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                </div>
                <div className={styles.col}>
                    <div className={styles.item}>
                        <div style={{ marginRight: '12px' }}>
                            <LetterAvatar
                                firstName={participant?.first_name || ''}
                                lastName={participant?.last_name || ''}
                                size={40}
                            />
                        </div>
                        <div className={styles.details}>
                            <div 
                                className={cn(styles.product, participant?.id && styles.clickable)}
                                onClick={() => participant?.id && navigate(`/admin/students/${participant.id}`, { state: { student: participant } })}
                            >
                                {participant ? `${participant.first_name} ${participant.last_name}` : 'Participant inconnu'}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.col}>
                    <div className={styles.contact}>
                        {participant?.email || 'Email non spécifié'}
                    </div>
                </div>
                <div className={styles.col}>
                    {getPresenceStatus(currentItem.status, currentItem.status_justification)}
                    {currentItem.comments && (
                        <div className={styles.commentIndicator} title={currentItem.comments}>
                            <Icon name="message" size="14" />
                        </div>
                    )}
                </div>
                <div className={styles.col}>
                    <Actions
                        className={styles.actions}
                        items={[
                            {
                                title: "Signature manuelle",
                                action: () => setShowSignModal(true)
                            },
                            {
                                title: "Passer absent",
                                action: () => handleStatusChange('Absent', '')
                            },
                            {
                                title: "Absence justifiée",
                                action: () => handleStatusChange('Absent', 'Absence justifiée')
                            },
                            {
                                title: "Retard",
                                action: () => handleStatusChange('En retard', '')
                            },
                            {
                                title: "Départ anticipé",
                                action: () => handleEarlyDeparture()
                            },
                            {
                                title: "Envoyer invitation signature",
                                action: () => onSendSignatureInvitation && onSendSignatureInvitation([item.id])
                            },
                            {
                                title: "Ecrire un commentaire",
                                action: () => setShowCommentModal(true)
                            }
                        ]}
                        small
                    />
                </div>
            </div>

            {showSignModal && (
                <ModalSignAttendance
                    visible={showSignModal}
                    onClose={() => setShowSignModal(false)}
                    attendance={currentItem}
                    session={session}
                    onSignatureSuccess={(attendanceId) => {
                        setShowSignModal(false);
                        // Notifier le parent pour rafraîchir les données
                        if (onAttendanceUpdate) {
                            onAttendanceUpdate(attendanceId);
                        }
                    }}
                    signerRole="admin"
                />
            )}

            {showCommentModal && (
                <ModalAttendanceComment
                    visible={showCommentModal}
                    onClose={() => setShowCommentModal(false)}
                    attendance={currentItem}
                    onSave={handleSaveComment}
                />
            )}
        </>
    );
};

export default Row;
